import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import { toast } from 'react-toastify';

const TrainingTab = () => {
  const { user, getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [watchedSeconds, setWatchedSeconds] = useState(0);
  const [lastPlayedSeconds, setLastPlayedSeconds] = useState(0);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);

  // Fetch documents for training
  useEffect(() => {
    const fetchDocumentsAndProgress = async () => {
      try {
        // Fetch documents
        const docRes = await fetch(`${API_URL}/documents?module=Training`, {
          headers: getAuthHeaders()
        });
        const docData = await docRes.json();
        setDocuments(docData.documents || []);
      } catch (error) {
        console.error('Error fetching training documents:', error);
      }
    };

    if (user) {
      fetchDocumentsAndProgress();
    }
  }, [user, getAuthHeaders]);

  const handleOpenDocument = (doc) => {
    setCurrentDocument(doc);
    setDocumentOpen(true);
  };

  const handleCloseDocument = () => {
    setDocumentOpen(false);
  };

  const handleOpenVideo = (doc) => {
    setCurrentDocument(doc);
    setVideoOpen(true);
    setWatchedSeconds(0);
    setLastPlayedSeconds(0);
    setQuizStarted(false);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
  };

  const handleVideoProgress = ({ playedSeconds }) => {
    const delta = playedSeconds - lastPlayedSeconds;
    if (delta > 0 && delta < 5) {
      setWatchedSeconds(prev => prev + delta);
    }
    setLastPlayedSeconds(playedSeconds);
  };

  const handleVideoEnd = () => {
    // Video completed, can show quiz if needed
  };

  const handleCloseVideo = () => {
    setVideoOpen(false);
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
  };

  const handleQuizAnswer = (questionId, answer) => {
    setQuizAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmitQuiz = () => {
    if (currentDocument && currentDocument.quiz) {
      const correctAnswers = currentDocument.quiz.questions.filter(
        (q) => quizAnswers[q.id] === q.correctAnswer
      ).length;
      const score = (correctAnswers / currentDocument.quiz.questions.length) * 100;
      setQuizScore(score);
      setQuizSubmitted(true);
    }
  };

  const handleModuleClick = (module) => {
    // If it's an actual training document, open it directly
    if (module.document) {
      if (module.document.docType === 'video/mp4') {
        handleOpenVideo(module.document);
      } else {
        handleOpenDocument(module.document);
      }
    } else {
      // For static modules, show the module info dialog
      setSelectedModule(module);
      setModuleDialogOpen(true);
    }
  };

  const handleStartTraining = () => {
    // Find the actual training document for this module
    const trainingDoc = documents.find(doc => 
      doc.documentName.toLowerCase().includes(selectedModule.title.toLowerCase()) ||
      doc.subModule.toLowerCase().includes(selectedModule.title.toLowerCase())
    );
    
    if (trainingDoc) {
      if (trainingDoc.docType === 'video/mp4') {
        handleOpenVideo(trainingDoc);
      } else {
        handleOpenDocument(trainingDoc);
      }
      setModuleDialogOpen(false);
    } else {
      toast.info('Starting training module...');
      setModuleDialogOpen(false);
    }
  };

  const renderQuiz = () => {
    if (!currentDocument || !currentDocument.quiz) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Training Quiz
        </Typography>
        {currentDocument.quiz.questions.map((question) => (
          <Box key={question.id} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {question.question}
            </Typography>
            <List>
              {question.options.map((option, index) => (
                <ListItem
                  key={index}
                  button
                  selected={quizAnswers[question.id] === index}
                  onClick={() => handleQuizAnswer(question.id, index)}
                  disabled={quizSubmitted}
                >
                  <ListItemText primary={option} />
                  {quizSubmitted && (
                    <Chip
                      size="small"
                      color={index === question.correctAnswer ? 'success' : 'error'}
                      label={index === question.correctAnswer ? 'Correct' : 'Incorrect'}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
        {!quizSubmitted ? (
          <Button
            variant="contained"
            onClick={handleSubmitQuiz}
            disabled={Object.keys(quizAnswers).length !== currentDocument.quiz.questions.length}
          >
            Submit Quiz
          </Button>
        ) : (
          <Alert severity={quizScore >= 80 ? 'success' : 'error'} sx={{ mt: 2 }}>
            Your score: {quizScore}%
            {quizScore < 80 && ' - You need 80% to pass. Please try again.'}
          </Alert>
        )}
      </Box>
    );
  };

  if (!user) return <div>Loading...</div>;

  // Only show actual training documents, sorted by submodule
  const trainingModules = documents.length > 0 ? documents.map((doc, index) => ({
    id: doc.documentId || index,
    title: doc.documentName,
    description: doc.description || 'Training material',
    document: doc, // Store the actual document for reference
    subModule: doc.subModule || 'General',
  })).sort((a, b) => a.subModule.localeCompare(b.subModule)) : [];

  // Group documents by submodule
  const groupedBySubModule = trainingModules.reduce((acc, module) => {
    if (!acc[module.subModule]) acc[module.subModule] = [];
    acc[module.subModule].push(module);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Training Modules
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Complete your training modules to enhance your skills and knowledge
        </Typography>
      </Box>

      {trainingModules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No training documents available at the moment.
          </Typography>
        </Box>
      ) : (
        Object.entries(groupedBySubModule).map(([subModule, modules]) => (
          <Box key={subModule} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ 
              mb: 3, 
              fontSize: '1.1rem', 
              fontWeight: 600,
              color: '#2c3e50',
              borderBottom: '2px solid #e8eaed',
              pb: 1,
            }}>
              {subModule}
            </Typography>
            <Grid container spacing={3}>
              {modules.map((module) => (
                <Grid item xs={12} md={6} key={module.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" component="div" sx={{ mb: 1 ,fontSize: '1rem'}}>
                        {module.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {module.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        startIcon={<PlayIcon />}
                        onClick={() => handleModuleClick(module)}
                        variant="contained"
                        fullWidth
                        sx={{fontSize: '0.9rem'}}
                      >
                        {module.document ? (
                          module.document.docType === 'video/mp4' ? 'Watch Video' : 'View Material'
                        ) : (
                          'Start Training'
                        )}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {/* Video Dialog */}
      <Dialog open={videoOpen} onClose={handleCloseVideo} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {currentDocument?.documentName}
          <IconButton aria-label="close" onClick={handleCloseVideo} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!quizStarted ? (
            <Box>
              <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                <ReactPlayer
                  url={`${API_URL}/documents/stream/${currentDocument?.filePath}`}
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  controls
                  onProgress={handleVideoProgress}
                  onEnded={handleVideoEnd}
                />
              </Box>
              {currentDocument?.quiz && (
                <Button
                  variant="contained"
                  onClick={handleStartQuiz}
                  sx={{ mt: 2 }}
                >
                  Start Quiz
                </Button>
              )}
            </Box>
          ) : (
            renderQuiz()
          )}
        </DialogContent>
      </Dialog>

      {/* Document Dialog */}
      <Dialog open={documentOpen} onClose={handleCloseDocument} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {currentDocument?.documentName}
          <IconButton
            aria-label="close"
            onClick={handleCloseDocument}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {currentDocument && (
            <>
              {/* Display images directly */}
              {currentDocument.docType && currentDocument.docType.startsWith('image/') ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img
                    src={`${API_URL}/documents/stream/${currentDocument.filePath || currentDocument.fileName}`}
                    alt={currentDocument.documentName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '600px',
                      objectFit: 'contain',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      console.error('Error loading image:', e);
                      e.target.style.display = 'none';
                      // Show error message
                      const errorDiv = document.createElement('div');
                      errorDiv.innerHTML = `
                        <div style="text-align: center; padding: 20px; color: #666;">
                          <p>Failed to load image</p>
                          <p>${currentDocument.documentName}</p>
                        </div>
                      `;
                      e.target.parentNode.appendChild(errorDiv);
                    }}
                  />
                </Box>
              ) : (
                /* Display PDFs and other documents in iframe */
                <iframe
                  src={`${API_URL}/documents/stream/${currentDocument.filePath || currentDocument.fileName}`}
                  width="100%"
                  height="600px"
                  title={currentDocument.documentName}
                  style={{ border: 'none' }}
                  onError={(e) => {
                    console.error('Error loading document:', e);
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Module Dialog */}
      <Dialog
        open={moduleDialogOpen}
        onClose={() => setModuleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedModule?.title}
          <IconButton
            aria-label="close"
            onClick={() => setModuleDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedModule && (
            <Box>
              <Typography variant="body1" paragraph>
                {selectedModule.description}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <AssignmentIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Module Content"
                    secondary="Video lessons, reading materials, and practical exercises"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <QuizIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Assessment"
                    secondary="Final quiz to test your understanding"
                  />
                </ListItem>
              </List>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Prerequisites:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStartTraining} variant="contained">
            Start Training
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingTab; 